
import React, { useMemo, useState, useEffect } from 'react';
import Select from 'react-select';
import { header } from './header.encry';

const PAGE_SIZE = 10;
const PRODUCT_PAGE_SIZE = 12;

function getGlovoPlatformId(plateforms) {
  if (!Array.isArray(plateforms)) return null;
  const glovo = plateforms.find(p => p.type === 'glovo');
  return glovo?._id?.$oid || glovo?._id || glovo?.id || null;
}


function getProductImage(product) {
  // Try url_product, image_url, picture, fallback
  const candidate = product?.url_product || product?.image_url || product?.picture;
  if (!candidate) return 'https://via.placeholder.com/120x120?text=No+Image';
  const value = String(candidate);
  if (/^https?:\/\//i.test(value)) return value;
  const base = (process.env.REACT_APP_DATA_PIC_PATH || '').replace(/\/$/, '');
  const path = value.replace(/^\//, '');
  return base ? `${base}/${path}` : value;
}

function normalizeRemoteId(id) {
  if (id == null) return '';
  let s = String(id).trim();
  // remove common prefixes that sometimes appear in external ids
  s = s.replace(/^(gid:|id:)/i, '');
  return s;
}
const PromotionsGlovoOverview = ({ glovoStores = [], allDiscounts = [], allProducts = [], glovoPlateformIds = [], productCategories = [] }) => {
  const [page, setPage] = useState(1);
  const [discounts, setDiscounts] = useState(Array.isArray(allDiscounts) ? allDiscounts : []);
  useEffect(() => setDiscounts(Array.isArray(allDiscounts) ? allDiscounts : []), [allDiscounts]);
  const [loadingDiscounts, setLoadingDiscounts] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creatingPromo, setCreatingPromo] = useState(false);
  const [selectedCatalogId, setSelectedCatalogId] = useState('');
  const [catalogOptions, setCatalogOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [discountPercent, setDiscountPercent] = useState(10);
  const [membershipEnabled, setMembershipEnabled] = useState(false);
  const [membershipPercent, setMembershipPercent] = useState(5);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [productModalList, setProductModalList] = useState([]);
  const [tempSelectedProducts, setTempSelectedProducts] = useState(new Set());
  const [productSearch, setProductSearch] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [productPage, setProductPage] = useState(1);
  const [cancellingMap, setCancellingMap] = useState({});
  const [cancelledSet, setCancelledSet] = useState(new Set());
  const [filterStatus, setFilterStatus] = useState('');

  const filteredDiscounts = useMemo(() => {
    if (!Array.isArray(discounts)) return [];
    if (!filterStatus) return discounts;
    const s = filterStatus.toString().toLowerCase();
    if (s === 'completed') {
      return discounts.filter(d => ['completed', 'done', 'finished'].includes((d.status || '').toString().toLowerCase()));
    }
    return discounts.filter(d => (d.status || '').toString().toLowerCase() === s);
  }, [discounts, filterStatus]);

  const total = filteredDiscounts.length;
  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;
  const paginated = useMemo(() => {
    if (!Array.isArray(filteredDiscounts)) return [];
    const start = (page - 1) * PAGE_SIZE;
    return filteredDiscounts.slice(start, start + PAGE_SIZE);
  }, [filteredDiscounts, page]);

  // Map storeCode to storeName for badge rendering
  const storeCodeToName = useMemo(() => {
    const map = {};
    glovoStores.forEach(store => {
      const code = String(store.storeCode || store.store_id || '');
      map[code] = store.storeName || store.name || code;
    });
    return map;
  }, [glovoStores]);

  // Build a lookup for products by remote_id for glovo plateform (normalized)
  const glovoPlatformIdSet = useMemo(() => new Set(glovoPlateformIds.map(id => String(id))), [glovoPlateformIds]);
  const productByRemoteId = useMemo(() => {
    const map = {};
    const add = (val, product) => {
      if (val == null) return;
      if (Array.isArray(val)) return val.forEach(v => add(v, product));
      const n = normalizeRemoteId(val);
      if (n) map[n] = product;
    };
    allProducts.forEach(product => {
      if (!Array.isArray(product.plateforms)) return;
      product.plateforms.forEach(pf => {
        const pfId = pf.plateform_id?.$oid || pf.plateform_id || pf.id;
        const pfType = (pf.type || pf.plateform_type || '').toString().toLowerCase();
        const isGlovo = pfType === 'glovo' || glovoPlatformIdSet.has(String(pfId));
        if (!isGlovo) return;

        add(pf.remote_id, product);
        add(pf.remoteId, product);
        add(pf.remote, product);
        add(pf.data?.id, product);

        if (Array.isArray(pf.roadmap)) {
          pf.roadmap.forEach(r => {
            add(r.data?.id, product);
            add(r.remote_id, product);
          });
        }
      });
    });
    return map;
  }, [allProducts, glovoPlatformIdSet]);

//   useEffect(() => {
//     try {
//       const count = Array.isArray(allProducts) ? allProducts.length : null;
//       console.debug('PromotionsGlovoOverview: allProducts count', count, Array.isArray(allProducts) ? allProducts.slice(0, 50) : allProducts);
//     } catch (err) {
//       console.debug('PromotionsGlovoOverview: failed to preview allProducts', err);
//     }
//   }, []);

  // Detect unmatched product ids referenced by discounts to help debugging
  const unmatchedProductIds = useMemo(() => {
    const set = new Set();
    (discounts || []).forEach(d => {
      const productObjs = Array.isArray(d.productMenuSelectionBehavior?.products)
        ? d.productMenuSelectionBehavior.products
        : [];
      productObjs.forEach(pObj => {
        const raw = (pObj && typeof pObj === 'object') ? (pObj.id ?? pObj._id ?? pObj) : pObj;
        const nid = normalizeRemoteId(raw);
        if (nid && !productByRemoteId[nid]) set.add(nid);
      });
    });
    const arr = Array.from(set);
    if (arr.length > 0) console.debug('PromotionsGlovoOverview: unmatched product remote ids', arr.slice(0, 200));
    return arr;
  }, [discounts, productByRemoteId]);

  const stats = useMemo(() => {
    const vendorSet = new Set();
    let active = 0, cancelled = 0, completed = 0;
    (discounts || []).forEach(d => {
      const s = (d.status || '').toString().toLowerCase();
      if (s === 'active') active += 1;
      else if (s === 'cancelled') cancelled += 1;
      else if (['completed', 'done', 'finished'].includes(s)) completed += 1;
      if (Array.isArray(d.vendorCodes)) d.vendorCodes.forEach(v => vendorSet.add(String(v)));
    });
    return { active, cancelled, completed, distinctVendors: vendorSet.size };
  }, [discounts]);

  const getFirstMatchingStoreName = (product) => {
    try {
      if (!product) return null;
      if (Array.isArray(product.plateforms)) {
        for (const pf of product.plateforms) {
          if (!Array.isArray(pf.roadmap)) continue;
          for (const r of pf.roadmap) {
            const vid = String(r.vendorId || r.vendor || '');
            if (vid && storeCodeToName[vid]) return storeCodeToName[vid];
          }
        }
      }
      if (Array.isArray(product.stores)) {
        for (const s of product.stores) {
          const storeId = String(s.store_id || s.storeId || s.store || '');
          if (storeId && storeCodeToName[storeId]) return storeCodeToName[storeId];
        }
      }
    } catch (err) { /* ignore */ }
    return null;
  };

  // compute catalog options from allProducts roadmap.catalogIds and include first store name
  useEffect(() => {
    const catalogFirstStore = new Map();
    (allProducts || []).forEach(p => {
      if (!Array.isArray(p.plateforms)) return;
      p.plateforms.forEach(pf => {
        if (!Array.isArray(pf.roadmap)) return;
        pf.roadmap.forEach(r => {
          const ids = r?.data?.catalogIds || [];
          if (!Array.isArray(ids)) return;
          ids.forEach(id => {
            const cid = String(id);
            if (!catalogFirstStore.has(cid)) {
              const vid = r.vendorId || r.vendor || null;
              const storeId = vid ? String(vid) : '';
              const storeName = storeId ? (storeCodeToName[storeId] || '') : '';
              catalogFirstStore.set(cid, { storeId, storeName });
            }
          });
        });
      });
    });
    const opts = Array.from(catalogFirstStore.entries()).map(([cid, meta]) => {
      const { storeId, storeName } = meta || {};
      let label = cid;
      if (storeName && storeId) label = `${cid} (${storeName} - ${storeId})`;
      else if (storeId) label = `${cid} (${storeId})`;
      setCatalogOptions(prev => prev); // placeholder to avoid lint false-positive
      return { value: cid, label };
    });
    setCatalogOptions(opts);
  }, [allProducts, storeCodeToName]);

  // compute vendor options when catalog changes
  useEffect(() => {
    if (!selectedCatalogId) { setVendorOptions([]); return; }
    const vendors = new Map();
    (allProducts || []).forEach(p => {
      if (!Array.isArray(p.plateforms)) return;
      p.plateforms.forEach(pf => {
        if (!Array.isArray(pf.roadmap)) return;
        pf.roadmap.forEach(r => {
          const ids = r?.data?.catalogIds || [];
          if (!Array.isArray(ids) || !ids.includes(selectedCatalogId)) return;
          const vid = r.vendorId || r.vendor || null;
          const storeName = vid ? storeCodeToName[String(vid)] : null;
          if (vid && storeName) vendors.set(String(vid), { id: String(vid), name: storeName });
        });
      });
      // also fallback to product.stores entries
      if (Array.isArray(p.stores)) p.stores.forEach(s => {
        const storeId = String(s.store_id || s.storeId || s.store || '');
        if (storeId) {
          // try to check if product has roadmap with catalog
          const has = (p.plateforms || []).some(pf => Array.isArray(pf.roadmap) && pf.roadmap.some(r => Array.isArray(r?.data?.catalogIds) && r.data.catalogIds.includes(selectedCatalogId)));
          const sname = storeCodeToName[storeId];
          if (has && sname) vendors.set(storeId, { id: storeId, name: sname });
        }
      });
    });
    // convert to react-select options
    setVendorOptions(Array.from(vendors.values()).map(v => ({ value: v.id, label: v.name })));
  }, [selectedCatalogId, allProducts, storeCodeToName]);

  // compute category options from productCategories for selected catalog
  useEffect(() => {
    if (!selectedCatalogId) { setCategoryOptions([]); setSelectedCategoryId(''); return; }
    const opts = (productCategories || []).filter(c => String(c.catalog_id) === String(selectedCatalogId) && (c.type === 'glovo' || c.type === undefined)).map(c => ({ value: String(c.remote_plateform_id || c.remote_plateform_id), label: `${c.name || c.remote_plateform_id || c._id} (${c.remote_plateform_id})` }));
    setCategoryOptions(opts);
    setSelectedCategoryId('');
  }, [productCategories, selectedCatalogId]);

  const openProductModal = () => {
    if (!selectedCatalogId || selectedVendors.length === 0) return;
    const list = [];
    (allProducts || []).forEach(p => {
      if (!Array.isArray(p.plateforms)) return;
      p.plateforms.forEach(pf => {
        if (!Array.isArray(pf.roadmap)) return;
          pf.roadmap.forEach(r => {
          if (!Array.isArray(r?.data?.catalogIds) || !r.data.catalogIds.includes(selectedCatalogId)) return;
          const vid = String(r.vendorId || r.vendor || '');
          if (!selectedVendors.includes(vid)) return;
          // category filtering: match selectedCategoryId against roadmap data.category.id or pf.category_remote_id
          const catId = String(r.data?.category?.id || r.data?.categories?.[0]?.id || pf.category_remote_id || '');
          if (selectedCategoryId && (!catId || catId !== String(selectedCategoryId))) return;
          // prefer roadmap data.id for payload
          const roadmapId = r.data?.id || (Array.isArray(r.remote_id) ? r.remote_id[0] : r.remote_id) || null;
          if (!roadmapId) return;
          const storeName = storeCodeToName[vid] || vid;
          list.push({ id: String(roadmapId), name: r.data?.name || p.name || '', vendor: vid, vendorName: storeName, image: (r.data?.images && r.data.images[0]?.url) || getProductImage(p), categoryId: catId });
        });
      });
    });
    // dedupe by id
    const seen = new Set();
    const dedup = list.filter(x => { if (!x.id) return false; if (seen.has(x.id)) return false; seen.add(x.id); return true; });
    setProductModalList(dedup);
    setTempSelectedProducts(new Set(selectedProducts));
    setShowProductModal(true);
  };

  const tempSelectAll = (yes) => {
    if (yes) setTempSelectedProducts(new Set(productModalList.map(p => p.id)));
    else setTempSelectedProducts(new Set());
  };

  const submitCreate = async () => {
    if (!selectedCatalogId || selectedVendors.length === 0 || selectedProducts.length === 0) return alert('Select catalog, vendors and at least one product');
    try {
      setCreatingPromo(true);
      const API_URL = process.env.REACT_APP_PEGASUS_API_URL || '';
      const startDate = new Date().toISOString().split('T')[0];
      const endDateObj = new Date(); endDateObj.setMonth(endDateObj.getMonth() + 3);
      const endDate = endDateObj.toISOString().split('T')[0];
      const payload = {
        globalEntityId: 'GV_MA',
        startDate,
        endDate,
        isRecurring: false,
        expeditionTypes: ['delivery'],
        vendorCode: String(selectedVendors[0]),
        discountPercentageRewardBehavior: { percentage: String(discountPercent) },
        productMenuSelectionBehavior: { rewardApplyLevel: 'product', products: selectedProducts.map(id => ({ id })) },
        selectedOutlets: selectedVendors,
        sponsorship: { ratios: [{ party: 'vendor', value: '1' }] },
      };
      if (membershipEnabled) payload.additionalBenefits = { membership: { discountPercentageRewardBehavior: { percentage: String(membershipPercent) } } };

      const res = await fetch(`${API_URL}/glovo-portal/promotions/create`, { method: 'POST', ...header(), body: JSON.stringify(payload) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || json?.message || `HTTP ${res.status}`);
      alert('Promotion created');
      setShowCreate(false);
      await fetchDiscounts();
    } catch (err) {
      console.error('Create promotion failed', err);
      alert('Create failed: ' + (err.message || err));
    } finally {
      setCreatingPromo(false);
    }
  };

  const fetchDiscounts = async () => {
    try {
      const API_URL = process.env.REACT_APP_PEGASUS_API_URL || '';
      const params = new URLSearchParams();
      params.append('limit', '100');
      const resp = await fetch(`${API_URL}/discounts?${params.toString()}`, {
        method: 'GET',
        ...header(),
      });
      const json = await resp.json().catch(() => ({}));
      if (resp.ok && json?.data) {
        setDiscounts(Array.isArray(json.data) ? json.data : []);
      } else {
        console.warn('Failed to refresh discounts', json);
      }
    } catch (err) {
      console.error('Error fetching discounts', err);
    }
  };

  const cancelPromotion = async (discount) => {
    const raw = discount.remote_id || discount.remoteId || discount.remote?.id || discount.id || null;
    if (!raw) return alert('No remote_id found for this discount');
    const remote = String(raw);
    const key = normalizeRemoteId(remote) || remote;
    try {
      setCancellingMap(prev => ({ ...prev, [key]: true }));
      const API_URL = process.env.REACT_APP_PEGASUS_API_URL || '';
      const res = await fetch(`${API_URL}/glovo-portal/promotions/cancel`, {
        method: 'POST',
        ...header(),
        body: JSON.stringify({ remote_id: remote }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json?.error || json?.message || `HTTP ${res.status}`);
      }
      setCancelledSet(prev => new Set(prev).add(key));
      console.debug('Promotion cancelled', json);
      // Refresh discounts list after successful cancellation
      await fetchDiscounts();
    } catch (err) {
      console.error('Failed to cancel promotion', err);
      alert('Failed to cancel promotion: ' + (err.message || err));
    } finally {
      setCancellingMap(prev => {
        const n = { ...prev };
        delete n[key];
        return n;
      });
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <h5 className="fw-bold mb-0">Promotions Glovo Overview</h5>
        <div className="d-flex align-items-center">
          <span className="badge bg-primary me-2">{total} total</span>
          <button className="btn btn-sm btn-outline-secondary me-2" onClick={fetchDiscounts} disabled={loadingDiscounts}>
            {loadingDiscounts ? <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Refresh</> : 'Refresh'}
          </button>
          <button className="btn btn-sm btn-primary" onClick={() => setShowCreate(true)}>Create Promotion</button>
        </div>
      </div>

      <div className="mb-3 d-flex gap-3">
        <div
          className={`card p-2 text-center ${filterStatus === 'active' ? 'border-primary' : ''}`}
          style={{ minWidth: 140, cursor: 'pointer' }}
          onClick={() => setFilterStatus(s => (s === 'active' ? '' : 'active'))}
        >
          <div className="small text-muted">Active</div>
          <div className="h5 mb-0">{stats.active}</div>
        </div>
        <div
          className={`card p-2 text-center ${filterStatus === 'cancelled' ? 'border-primary' : ''}`}
          style={{ minWidth: 140, cursor: 'pointer' }}
          onClick={() => setFilterStatus(s => (s === 'cancelled' ? '' : 'cancelled'))}
        >
          <div className="small text-muted">Cancelled</div>
          <div className="h5 mb-0">{stats.cancelled}</div>
        </div>
        <div
          className={`card p-2 text-center ${filterStatus === 'completed' ? 'border-primary' : ''}`}
          style={{ minWidth: 140, cursor: 'pointer' }}
          onClick={() => setFilterStatus(s => (s === 'completed' ? '' : 'completed'))}
        >
          <div className="small text-muted">Completed</div>
          <div className="h5 mb-0">{stats.completed}</div>
        </div>
        <div className="card p-2 text-center" style={{ minWidth: 160 }}>
          <div className="small text-muted">Distinct Stores</div>
          <div className="h5 mb-0">{stats.distinctVendors}</div>
        </div>
      </div>
      {/* Create promotion panel/modal */}
      {showCreate && (
        <div style={{ border: '1px solid #e9ecef', padding: 12, borderRadius: 6, marginBottom: 12, background: '#fff' }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong>Create Promotion</strong>
            <div>
              <button className="btn btn-sm btn-secondary me-2" onClick={() => setShowCreate(false)}>Close</button>
              <button className="btn btn-sm btn-success" onClick={submitCreate} disabled={creatingPromo}>{creatingPromo ? 'Creating...' : 'Create'}</button>
            </div>
          </div>
          <div className="row gy-2">
            <div className="col-3">
              <label className="form-label">Catalog</label>
              <Select
                options={catalogOptions}
                value={catalogOptions.find(o => o.value === selectedCatalogId) || null}
                onChange={opt => setSelectedCatalogId(opt ? opt.value : '')}
                isClearable
                placeholder="Select catalog"
              />
            </div>
            <div className="col-3">
              <label className="form-label">Category</label>
              <Select
                options={categoryOptions}
                value={categoryOptions.find(o => o.value === selectedCategoryId) || null}
                onChange={opt => setSelectedCategoryId(opt ? opt.value : '')}
                isClearable
                placeholder="Filter by category"
                isDisabled={!categoryOptions || categoryOptions.length===0}
              />
            </div>
            <div className="col-4">
              <label className="form-label">Vendors (stores)</label>
              <Select
                isMulti
                options={vendorOptions}
                value={vendorOptions.filter(o => selectedVendors.includes(o.value))}
                onChange={opts => setSelectedVendors(Array.isArray(opts) ? opts.map(o => o.value) : [])}
                placeholder="Select stores"
              />
            </div>
            <div className="col-2">
              <label className="form-label">Discount %</label>
              <div>
                {Array.from({ length: 10 }).map((_, i) => {
                  const val = (i+1)*5;
                  return (<button key={val} type="button" className={`btn btn-sm me-1 ${discountPercent===val ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setDiscountPercent(val)}>{val}%</button>);
                })}
              </div>
            </div>
            <div className="col-3">
              <label className="form-label">Prime membership</label>
              <div className="d-flex align-items-center">
                <input type="checkbox" className="form-check-input me-2" checked={membershipEnabled} onChange={e => setMembershipEnabled(e.target.checked)} />
                <select className="form-select" value={membershipPercent} onChange={e => setMembershipPercent(Number(e.target.value))} disabled={!membershipEnabled}>
                  <option value={5}>5%</option>
                  <option value={10}>10%</option>
                </select>
              </div>
            </div>

            <div className="col-12 mt-2">
              <button className="btn btn-sm btn-outline-primary me-2" onClick={() => openProductModal()} disabled={!selectedCatalogId || selectedVendors.length===0}>Select Products</button>
              <span className="small text-muted">{selectedProducts.length} products selected</span>
            </div>
          </div>
        </div>
      )}

      {/* Product selection modal (simple) */}
      {showProductModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1050 }} onClick={() => setShowProductModal(false)}>
          <div style={{ width: '80%', maxHeight: '80%', margin: '5% auto', background: '#fff', padding: 16, overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="d-flex justify-content-between mb-2">
              <strong>Select Products</strong>
              <div>
                <button className="btn btn-sm btn-secondary me-2" onClick={() => setShowProductModal(false)}>Close</button>
                <button className="btn btn-sm btn-primary" onClick={() => { setShowProductModal(false); setSelectedProducts(Array.from(tempSelectedProducts)); }}>Apply</button>
              </div>
            </div>
            <div className="mb-2 d-flex gap-2 align-items-center">
              <div style={{ minWidth: 260, flex: 1 }}>
                <input className="form-control form-control-sm" placeholder="Search products by name" value={productSearch} onChange={e => { setProductSearch(e.target.value); setProductPage(1); }} />
              </div>
              <div style={{ width: 220 }}>
                <Select
                  options={categoryOptions}
                  value={categoryOptions.find(o => o.value === selectedCategoryId) || null}
                  onChange={opt => { setSelectedCategoryId(opt ? opt.value : ''); setProductPage(1); }}
                  isClearable
                  placeholder="Category"
                />
              </div>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => { tempSelectAll(true); }}>Select all</button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => { tempSelectAll(false); }}>Clear</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              {(() => {
                const filtered = productModalList.filter(p => {
                  if (productSearch && !(p.name || '').toLowerCase().includes(productSearch.toLowerCase())) return false;
                  if (selectedCategoryId && String(p.categoryId || '') !== String(selectedCategoryId)) return false;
                  return true;
                });
                const total = filtered.length;
                const totalPages = Math.max(1, Math.ceil(total / PRODUCT_PAGE_SIZE));
                const page = Math.min(Math.max(1, productPage), totalPages);
                const start = (page - 1) * PRODUCT_PAGE_SIZE;
                const pageItems = filtered.slice(start, start + PRODUCT_PAGE_SIZE);
                return (
                  <>
                    {pageItems.map(p => {
                      const checked = tempSelectedProducts.has(p.id);
                      return (
                        <div key={p.id} className={`card p-2 ${checked ? 'border-primary' : ''}`} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }} onClick={() => { if (checked) { tempSelectedProducts.delete(p.id); } else { tempSelectedProducts.add(p.id); } setTempSelectedProducts(new Set(tempSelectedProducts)); }}>
                          <div style={{ width: 64, height: 64, flex: '0 0 64px' }}>
                            <img src={p.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} onError={e => e.target.src = 'https://via.placeholder.com/120x120?text=No+Image'} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                            <div className="small text-muted">{p.vendorName || p.vendor}</div>
                            <div className="small text-muted">ID: {p.id}</div>
                          </div>
                          <div style={{ flex: '0 0 28px' }}>
                            <input type="checkbox" checked={checked} readOnly className="form-check-input" />
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ gridColumn: '1/-1', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 12 }}>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setProductPage(p => Math.max(1, p - 1))} disabled={productPage <= 1}>Prev</button>
                      <span className="small text-muted">Page {productPage} / {totalPages} — {total} items</span>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setProductPage(p => Math.min(totalPages, p + 1))} disabled={productPage >= totalPages}>Next</button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      <div className="table-responsive" style={{ maxWidth: '100%' }}>
        <table className="table table-bordered align-middle" style={{ width: '100%', maxWidth: '100%' }}>
          <thead className="table-light">
            <tr>
              <th>Title</th>
              <th>Status</th>
              <th>Stores</th>
              <th>Discount</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr><td colSpan={4} className="text-center text-muted">No discounts found</td></tr>
            ) : paginated.flatMap((d, idx) => {
              const discountId = d._id?.$oid || d._id || d.id || idx;
              // Get product ids for this discount
              const productObjs = Array.isArray(d.productMenuSelectionBehavior?.products)
                ? d.productMenuSelectionBehavior.products
                : [];
              const productCount = Array.isArray(productObjs) ? productObjs.length : 0;
              // build per-store product counts based on matched product data
              const storeCounts = {};
              if (Array.isArray(d.vendorCodes)) d.vendorCodes.forEach(code => { storeCounts[String(code)] = 0; });
              if (Array.isArray(productObjs)) {
                productObjs.forEach((pObj, i) => {
                  const raw = (pObj && typeof pObj === 'object') ? (pObj.id ?? pObj._id ?? pObj) : pObj;
                  const pid = normalizeRemoteId(raw) || `missing-${i}`;
                  const product = productByRemoteId[pid];
                  if (!product) return;
                  // try to find vendor ids from product plateforms/roadmap
                  let matched = false;
                  if (Array.isArray(product.plateforms)) {
                    product.plateforms.forEach(pf => {
                      if (!Array.isArray(pf.roadmap)) return;
                      pf.roadmap.forEach(r => {
                        const vid = String(r.vendorId || r.vendor || '');
                        if (vid && storeCounts.hasOwnProperty(vid)) { storeCounts[vid] = (storeCounts[vid] || 0) + 1; matched = true; }
                      });
                    });
                  }
                  if (!matched && Array.isArray(product.stores)) {
                    product.stores.forEach(s => {
                      const storeId = String(s.store_id || s.storeId || s.store || '');
                      if (storeId && storeCounts.hasOwnProperty(storeId)) storeCounts[storeId] = (storeCounts[storeId] || 0) + 1;
                    });
                  }
                });
              }
              // For each product in the discount, show info if found, else show placeholder
              return [
                <tr  key={discountId}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div>{d.title || d.name || '—'}</div>
                      {/** Cancel button and inline badges */}
                      <div className="d-flex align-items-center">
                        {Array.isArray(d.vendorCodes) && d.vendorCodes.length > 0 ? (
                          d.vendorCodes.map(code => (
                            <span key={String(code)} className="badge bg-info text-dark me-1">
                              {storeCodeToName[String(code)] || code}
                              <span className="ms-1">({storeCounts[String(code)] || 0})</span>
                            </span>
                          ))
                        ) : null}
                        {typeof d.discountPercentageRewardBehavior?.percentage !== 'undefined' && d.discountPercentageRewardBehavior?.percentage !== null ? (
                          <span className="badge bg-primary ms-1">-{d.discountPercentageRewardBehavior.percentage}%</span>
                        ) : null}
                        {(() => {
                          const raw = d.remote_id || d.remoteId || d.remote?.id || d.id || '';
                          const key = normalizeRemoteId(raw) || String(raw || '');
                          const isCancelling = !!cancellingMap[key];
                          const isCancelled = cancelledSet.has(key) || d.status === 'cancelled';
                          return (
                            <button
                              type="button"
                              className={`btn btn-sm btn-outline-danger ms-2 ${isCancelling ? 'disabled' : ''}`}
                              onClick={() => cancelPromotion(d)}
                              disabled={isCancelling || isCancelled}
                              title={isCancelled ? 'Promotion cancelled' : 'Annuler la promotion'}
                            >
                              {isCancelling ? 'Annuler...' : isCancelled ? 'Annulé' : 'Annuler la promotion'}
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="mt-1" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span className={`badge bg-${d.status === 'active' ? 'success' : d.status === 'inactive' ? 'secondary' : 'warning'} me-1`}>{d.status}</span>
                      <span className="badge bg-light text-dark">Products: {productCount}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`badge bg-${d.status === 'active' ? 'success' : d.status === 'inactive' ? 'secondary' : 'warning'}`}>{d.status}</span>
                  </td>
                  <td>
                    {Array.isArray(d.vendorCodes) && d.vendorCodes.length > 0 ? (
                      d.vendorCodes.map(code => (
                        <span key={code} className="badge bg-info text-dark me-1">
                          {storeCodeToName[String(code)] || code}
                          <span className="ms-1">({storeCounts[String(code)] || 0})</span>
                        </span>
                      ))
                    ) : (
                      <span className="text-muted small">—</span>
                    )}
                  </td>
                  <td>
                    <span className="badge bg-primary">-{d.discountPercentageRewardBehavior?.percentage || 0}%</span>
                  </td>
                </tr>,
                <tr key={discountId + '-products'}>
                  <td colSpan={4} style={{ padding: 0, background: '#f8f9fa', overflowX: 'auto' }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'nowrap',
                        gap: 16,
                        alignItems: 'flex-start',
                        padding: '8px',
                        overflowX: 'auto',
                        minHeight: 120,
                      }}
                    >
                      {productObjs.length === 0 ? (
                        <span className="text-muted small ms-3">No products for this discount</span>
                      ) : (
                        productObjs.map((pObj, i) => {
                          const raw = (pObj && typeof pObj === 'object') ? (pObj.id ?? pObj._id ?? pObj) : pObj;
                          const pid = normalizeRemoteId(raw) || `missing-${i}`;
                          const product = productByRemoteId[pid];
                          return (
                            <div key={`${discountId}-prod-${pid}-${i}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 100, flex: '0 0 auto' }}>
                              <img
                                src={product ? getProductImage(product) : 'https://via.placeholder.com/120x120?text=No+Image'}
                                alt={product ? (product.name || 'Product') : pid}
                                style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd', background: '#fff' }}
                                onError={e => { e.target.src = 'https://via.placeholder.com/120x120?text=No+Image'; }}
                              />
                              <div className="small mt-1" style={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {product ? (product.name || product.productName || '—') : pid}
                                  {product ? (<div className="small text-muted">{getFirstMatchingStoreName(product) || ''}</div>) : null}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </td>
                </tr>
              ];
            })}
          </tbody>
        </table>
      </div>
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <nav className="mt-3">
          <ul className="pagination justify-content-center mb-0">
            <li className={`page-item${page === 1 ? ' disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage(page - 1)} disabled={page === 1}>&laquo;</button>
            </li>
            {Array.from({ length: totalPages }, (_, i) => (
              <li key={i + 1} className={`page-item${page === i + 1 ? ' active' : ''}`}>
                <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
              </li>
            ))}
            <li className={`page-item${page === totalPages ? ' disabled' : ''}`}>
              <button className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages}>&raquo;</button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default PromotionsGlovoOverview;
